// ============================================
// INFRASTRUCTURE - IndexedDB con Dexie (Outbox Offline)
// ============================================

import Dexie, { Table } from 'dexie';
import type { CreateVentaDto } from '../../domain/dtos';

// ---------- Definición de tablas ----------
export interface OutboxVenta {
  id?: number; // Auto-incremental
  localId: string; // UUID generado localmente
  dto: CreateVentaDto;
  status: 'pending' | 'syncing' | 'synced' | 'error';
  errorMessage?: string;
  retryCount: number;
  creadoEn: string;
  intentoSyncEn?: string;
}

export interface SyncMetadata {
  id: string;
  ultimaSincronizacion: string;
}

// ---------- Database ----------
export class AlmacenesDB extends Dexie {
  outboxVentas!: Table<OutboxVenta, number>;
  syncMetadata!: Table<SyncMetadata, string>;

  constructor() {
    super('AlmacenesDigitalesDB');

    this.version(1).stores({
      outboxVentas: '++id, localId, status, creadoEn',
      syncMetadata: 'id',
    });
  }
}

export const db = new AlmacenesDB();

// ---------- Outbox Service ----------
export const outboxService = {
  /**
   * Agregar venta al outbox (offline)
   */
  async addVenta(dto: CreateVentaDto): Promise<string> {
    const localId = crypto.randomUUID();
    
    await db.outboxVentas.add({
      localId,
      dto,
      status: 'pending',
      retryCount: 0,
      creadoEn: new Date().toISOString(),
    });

    return localId;
  },

  /**
   * Obtener ventas pendientes de sincronización
   */
  async getPendingVentas(): Promise<OutboxVenta[]> {
    return await db.outboxVentas
      .where('status')
      .anyOf(['pending', 'error'])
      .toArray();
  },

  /**
   * Marcar venta como en proceso de sincronización
   */
  async markAsSyncing(id: number): Promise<void> {
    await db.outboxVentas.update(id, {
      status: 'syncing',
      intentoSyncEn: new Date().toISOString(),
    });
  },

  /**
   * Marcar venta como sincronizada exitosamente
   */
  async markAsSynced(id: number): Promise<void> {
    await db.outboxVentas.update(id, {
      status: 'synced',
    });
  },

  /**
   * Marcar venta como error
   */
  async markAsError(id: number, errorMessage: string): Promise<void> {
    const venta = await db.outboxVentas.get(id);
    if (!venta) return;

    await db.outboxVentas.update(id, {
      status: 'error',
      errorMessage,
      retryCount: venta.retryCount + 1,
    });
  },

  /**
   * Eliminar ventas ya sincronizadas (limpieza)
   */
  async cleanSynced(): Promise<number> {
    return await db.outboxVentas.where('status').equals('synced').delete();
  },

  /**
   * Contar ventas pendientes
   */
  async countPending(): Promise<number> {
    return await db.outboxVentas
      .where('status')
      .anyOf(['pending', 'error'])
      .count();
  },

  /**
   * Obtener última sincronización
   */
  async getLastSync(): Promise<string | null> {
    const metadata = await db.syncMetadata.get('lastSync');
    return metadata?.ultimaSincronizacion || null;
  },

  /**
   * Actualizar última sincronización
   */
  async updateLastSync(): Promise<void> {
    await db.syncMetadata.put({
      id: 'lastSync',
      ultimaSincronizacion: new Date().toISOString(),
    });
  },
};
